<template>
  <!-- Content -->
  <div v-if="loaded" class="lg:ml-56 lg:mr-44">
    <!-- showFontPreview Modal -->
    <div
      v-if="showDeleteModal"
      id="showDeleteModal"
      class="fixed z-70 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <!-- Modal panel, show/hide based on modal state. -->
        <div
          class="w-full max-w-4xl inline-block align-bottom bg-white rounded-lg p-6 text-left overflow-visible shadow transform transition-all sm:mt-8 sm:mb-20 sm:align-middle sm:max-w-3xl sm:w-full sm:p-16 relative"
        >
          <h2 class="w-full mb-4"><span>Delete page</span></h2>

          <p class="mb-4 text-md">Do you want to delete this page forever?</p>

          <button
            :disabled="deleteThinking"
            type="submit"
            class="btn btn-primary m-0 relative"
            @click="deletePage"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie
                v-if="deleteThinking"
                :options="loadingAnimationOptions"
              />
            </div>
            <span :class="[deleteThinking ? 'invisible' : '']">Confirm</span>
          </button>

          <button
            type="button"
            class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
            @click="showDeleteModal = !showDeleteModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div
      class="post-intro bg-bluegray-700 h-120 w-full relative z-10 text-white pb-52 flex flex-col justify-end overflow-x-hidden"
    >
      <h1 class="ml-6 lg:ml-16">Edit Page</h1>
    </div>

    <div
      class="bg-bluegray-100 w-full pb-16 flex flex-col items-center lg:items-start"
    >
      <!-- Page content -->
      <div
        class="bg-white rounded-xl lg:ml-16 -mt-48 relative z-20 py-16 px-6 lg:p-16 w-11/12 lg:w-11/12"
      >
        <div class="w-full mb-8 -mt-4 pb-8 border-b">
          <div class="flex space-x-2 justify-start lg:justify-between mb-4">
            <!-- Save Changes Button -->
            <button
              :disabled="!canSubmit || saveThinking"
              type="submit"
              @click="submitUpdates"
            >
              <div
                :class="[
                  !canSubmit
                    ? 'opacity-25 border-black border cursor-not-allowed'
                    : 'btn-primary',
                  saveThinking ? 'thinking-primary cursor-not-allowed' : '',
                ]"
                class="relative btn m-0"
              >
                <div
                  class="absolute inset-0 w-full flex justify-center items-center z-20"
                >
                  <lottie
                    v-if="saveThinking"
                    :options="loadingAnimationOptions"
                  />
                </div>
                <span :class="[saveThinking ? 'invisible' : '']">Save</span>
              </div>
            </button>

            <button
              type="submit"
              class="btn btn-black m-0"
              @click="showDeleteModal = !showDeleteModal"
            >
              <span>Delete</span>
            </button>
          </div>

          <!-- Publish Toggle -->
          <div class="flex justify-start items-center space-x-2 w-28">
            <button
              type="button"
              :class="[
                publish_status ? 'bg-primary' : 'bg-gray-200',
                toggleClass,
              ]"
              role="switch"
              aria-checked="false"
              @click="publish_status = !publish_status"
            >
              <span
                aria-hidden="true"
                class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                :class="[publish_status ? 'translate-x-5' : 'translate-x-0']"
              ></span>
            </button>
            <span v-if="publish_status">Publish</span>
            <span v-else>Draft</span>
          </div>
        </div>

        <div class="w-full h-full flex flex-wrap lg:flex-nowrap">
          <!-- Left Col -->
          <div
            class="w-full lg:w-4/12 pr-4 pb-8 lg:pb-0 mb-8 lg:mb-0 border-b lg:border-b-0 border-bluegray-300 transition-all duration-300"
          >
            <!-- Post Title -->
            <div :class="inputWrapperClass" class="h-12">
              <h6 :class="inputLabelClass">Title</h6>
              <input
                id="title"
                v-model="page_title"
                type="text"
                name="title"
                autocomplete="title"
                :class="inputTextClass"
              />
            </div>

            <!-- Page link title -->
            <div :class="inputWrapperClass" class="h-12">
              <h6 :class="inputLabelClass" class="flex items-center group">
                Link title (optional)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-3 w-3 ml-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div :class="infoClass">
                  Creates a button linking to an external resource or website
                </div>
              </h6>
              <input
                id="page_link_title"
                v-model="page_link_title"
                type="text"
                name="page_link_title"
                autocomplete="page_link_title"
                :class="inputTextClass"
              />
            </div>

            <!-- Page Link URL -->
            <div :class="inputWrapperClass" class="h-12 -mt-4">
              <h6 :class="inputLabelClass">Link URL (optional)</h6>
              <input
                id="page_link_url"
                v-model="page_link_url"
                type="text"
                name="page_link_url"
                autocomplete="page_link_url"
                :class="inputTextClass"
              />
              <p v-if="!valid_link_url" class="text-xs text-red-500">
                Please provide a valid URL
              </p>
            </div>
          </div>
          <!-- /Left Col -->

          <!-- Right Col -->
          <div
            class="w-full lg:w-8/12 lg:border-l border-bluegray-300 lg:pl-6 transition-all duration-300 ease-in-out"
          >
            <!-- Featured image -->
            <div class="py-4 w-full relative mr-0 lg:mr-2">
              <label
                for="featuredImage"
                class="block text-sm font-medium text-gray-700"
                >Featured image</label
              >
              <div class="mt-1 flex items-center">
                <div
                  :class="imgWrapperClass"
                  :style="{ backgroundImage: `url(${previewImage})` }"
                  class="bg-gray-500"
                >
                  <button
                    v-if="!previewImageChanged"
                    type="button"
                    :class="imgButtonClass"
                    @click="changeImage"
                  >
                    Change
                  </button>
                  <button
                    v-else
                    type="button"
                    :class="imgButtonClass"
                    @click="resetImage"
                  >
                    Cancel
                  </button>
                  <span class="hidden">{{ settings.profile_title }}</span
                  ><!-- Required ... spoof the dom -->
                </div>
                <form ref="selectImageForm">
                  <input
                    ref="selectImage"
                    type="file"
                    class="hidden"
                    @input="imageSelected"
                  />
                </form>
              </div>
              <div class="text-xs text-bluegray-500 mb-4">
                .jpg or .png format with a maximum file size of 1mb
              </div>
              <div v-if="previewImage" class="relative flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="page_image_featured_remove"
                    v-model="page_image_featured_remove"
                    name="page_image_featured_remove"
                    type="checkbox"
                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 rounded"
                  />
                </div>
                <div class="ml-3 text-sm">
                  <label
                    for="page_image_featured_remove"
                    class="font-medium text-gray-700"
                    >Remove</label
                  >
                </div>
              </div>
            </div>
            <!-- /Featured image -->

            <!-- Body -->
            <div class="my-8">
              <h6>Page Content</h6>
              <p
                v-if="badbrowser"
                class="text-red-500 text-xs my-1 w-full flex justify-start items-center space-x-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  >We recommend using Chrome to best utilise all features in
                  this editor.</span
                >
              </p>
              <Editor
                :key="page_body_original"
                :initial-content="page_body_original"
                :active-features="[
                  'headings',
                  'bold',
                  'italic',
                  'strike',
                  'highlight',
                  'text_colour',
                  'underline',
                  'h1',
                  'h2',
                  'h3',
                  'alignments',
                  'alignLeft',
                  'alignCenter',
                  'alignRight',
                  'lists',
                  'bulletList',
                  'orderedList',
                  'blockquote',
                  'code',
                  'codeBlock',
                  'link',
                  'remove_link',
                  'image',
                  'tables',
                  'table',
                  'horizontalRule',
                  'undo',
                  'redo',
                ]"
                @update-content="updateEditorContent"
              />
            </div>
            <!-- /Body -->
          </div>
          <!-- /Right Col -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../js/plugins/event-bus.js'
import { mapActions, mapGetters } from 'vuex'
import { shared } from '../../../js/mixins/shared.js'
import Editor from '../../../careerwise/tenant/manager/components/editor-wysiwyg/Editor.vue'
import 'vue2-datepicker/index.css'
import * as loadingAnimation from '../../../shared/components/loading.json'

export default {
  components: {
    Editor,
  },

  mixins: [shared],

  props: {
    post_id: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  data() {
    return {
      badbrowser: false,

      showDeleteModal: false,

      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      deleteThinking: false,
      saveThinking: false,

      loaded: false,

      canSubmit: false,

      page_status: 1,
      publish_status: true,
      page_status_original: 1,

      page_title: '',
      page_title_original: '',

      page_body: '',
      page_body_original: '',

      page_image_featured: null,
      page_image_featured_original: null,
      previewImage: null,
      previewImageChanged: false,
      page_image_featured_remove: false,

      page_link_title: '',
      page_link_title_original: '',

      page_link_url: '',
      page_link_url_original: '',
      valid_link_url: true,

      mandatory_options: ['title'],
      update: [], // @geoff update is now an array that we push or remove anythign that has changed...

      //input styles
      toggleClass:
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
      inputWrapperClass:
        'rounded relative bg-white shadow border border-bluegray-300 mb-8 ',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-bluegray-700',
      inputTextClass:
        'flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-bluegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 mt-5 outline-none focus:outline-none bg-transparent h-24 font-primary text-black  focus:placeholder-bluegray-300 transition-all duration-100 ease-in-out w-full',
      ddButtonClass:
        'w-full flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-12 font-primary text-black cursor-pointer',
      ddSvgClass: 'cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2',
      ddSpanClass: 'block text-base font-primary truncate text-left mt-2',
      ddUlClass:
        'no-scrollbar transform ease-in-out duration-300 max-h-56 shadow-md px-6 w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl',
      ddLiClass:
        'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
      imgWrapperClass:
        'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center shadow-sm relative z-20',
      imgButtonClass:
        'z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6',
      infoClass:
        'absolute left-full top-0 w-28 z-50 border border-bluegray-300 rounded py-2 px-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white',
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
    }),
  },

  display: 'Nested',
  order: 15,

  watch: {
    post_id: {
      handler: function (id) {
        this.getPost(id)
      },

      deep: true,
    },

    page_title: {
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        if (val === this.page_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('title')) {
            this.update.push('title')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_body: {
      // Integer option
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        if (val === this.page_body_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'body') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('body')) {
            this.update.push('body')
          }
        }
        this.processSubmitData()
      },

      deep: true,
    },

    page_image_featured: {
      // image option
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        // Only allow if we have interacted with the image:

        if (!this.previewImageChanged) {
          return
        }

        if (!val) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'image_featured') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('image_featured')) {
            this.update.push('image_featured')
          }
        }
        this.processSubmitData()
      },

      deep: true,
    },

    page_image_featured_remove: {
      // Checkbox option remove another data point
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        if (!val) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'image_remove') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('image_remove')) {
            this.update.push('image_remove')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_link_title: {
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        if (val === this.page_link_title_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_title') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_title')) {
            this.update.push('link_title')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    page_link_url_original: {
      handler: function (url) {
        if (!this.loaded) {
          return
        }
        if (!url) {
          url = ''
          this.$set(this, 'page_link_url_original', url)
        }
      },

      deep: true,
    },

    page_link_url: {
      handler: function (url) {
        if (!this.loaded) {
          return
        }

        if (!url) {
          url = ''
        }

        if (url.length > 0) {
          if (!this.validateURL(url)) {
            this.$set(this, 'valid_link_url', false)
          } else {
            if (!this.validateURLProtocol(url)) {
              url = this.$set(
                this,
                'page_link_url',
                this.updateURLWithProtocol(url),
              )
            }
            this.$set(this, 'valid_link_url', true)
          }
        } else {
          this.$set(this, 'valid_link_url', true)
        }

        if (url === this.page_link_url_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'link_url') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('link_url')) {
            this.update.push('link_url')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },

    publish_status: {
      handler: function (val) {
        if (!this.loaded) {
          return
        }

        if (val) {
          val = 1
        } else {
          val = 0
        }

        this.$set(this, 'page_status', val)

        if (val === this.page_status_original) {
          for (let i = 0; i < this.update.length; i++) {
            if (this.update[i] === 'status') {
              this.update.splice(i, 1)
            } // Remove this from the update
          }
        } else {
          if (!this.update.includes('status')) {
            this.update.push('status')
          }
        }

        this.processSubmitData()
      },

      deep: true,
    },
  },

  mounted() {
    // Determine if we need to show a browser warning...
    let badbrowser = document.getElementsByTagName('body')[0].dataset.badbrowser
    if (badbrowser) {
      this.$set(this, 'badbrowser', true)
    }

    this.getPost(this.post_id)
  },

  methods: {
    ...mapActions({
      dispatchSetMenu: 'shared/dispatchSetMenu',
    }),

    updateEditorContent(val) {
      this.$set(this, 'page_body', val)
    },

    processSubmitData() {
      let canSubmit = this.update.length > 0 ? true : false

      // Check validation = mandatory options are met | reset to zero if any are not met
      Object.entries(this.update).map(([key, value]) => {
        let required = this.mandatory_options.includes(key)
        if (value === null && required) {
          canSubmit = 0
        }
      })

      if (canSubmit) {
        this.$set(this, 'canSubmit', true)
      } else {
        this.$set(this, 'canSubmit', false)
      }
    },

    // IMAGE METHODS:

    changeImage() {
      this.$refs.selectImage.click()
    },

    imageSelected() {
      let input = this.$refs.selectImage
      let file = input.files

      if (file && file[0]) {
        if (file[0].size > 1024 * 1024 * 1) {
          EventBus.$emit('triggerAlertResponse', {
            status: 'error',
            message: 'File size too large. 1mb file size limit exceeded.',
          })
          return
        }

        let reader = new FileReader()

        reader.onload = (e) => {
          this.$set(this, 'previewImageChanged', true)
          this.$set(this, 'page_image_featured', file[0]) // @geoff this needs to go below previewImageChanged

          this.$set(this, 'page_image_featured_remove', false)
          // this.$set(this,'page_image_featured',e.target.result)
          this.previewImage = e.target.result
        }

        reader.readAsDataURL(file[0])
        this.$emit('input', file[0])
      }
    },

    resetImage() {
      this.$set(this, 'previewImageChanged', false)
      this.$set(this, 'page_image_featured', null)
      this.$set(this, 'previewImage', this.page_image_featured_original)
      this.$refs.selectImageForm.reset()
    },

    //
    returnDataNodeValue(key) {
      let value = null
      switch (key) {
        case 'status':
          value = this.page_status
          break
        case 'title':
          value = this.page_title
          break
        case 'body':
          value = this.page_body
          break
        case 'image_featured':
          value = this.page_image_featured
          break
        case 'link_url':
          value = this.page_link_url
          break
        case 'link_title':
          value = this.page_link_title
          break
        case 'image_remove':
          value = this.page_image_featured_remove
          break
        default:
          break
      }
      return value
    },

    // SAVE API

    submitUpdates() {
      if (!this.canSubmit) {
        return
      }

      let submitData = new FormData()
      let score = 0
      Object.entries(this.update).map(([, value]) => {
        let skip = false
        if (value === 'body') {
          if (this.page_body.length < 1) {
            submitData.append('body_clear', true)
            skip = true
          }
        }

        if (value === 'link_title') {
          if (this.page_link_title.length < 1) {
            submitData.append('link_clear', true)
            skip = true
          }
        }
        if (value === 'link_url') {
          if (this.page_link_url.length < 1) {
            submitData.append('link_clear', true)
            skip = true
          }
        }

        if (!skip) {
          let val = this.returnDataNodeValue(value)

          if (
            (value === 'page_link_url' || value === 'page_link_title') &&
            val.length < 1
          ) {
            switch (value) {
              case 'page_link_url':
                value = 'remove_link_url'
                val = true
                break

              case 'page_link_title':
                value = 'remove_link_title'
                val = true
                break

              default:
                break
            }
          }

          if (val !== null || val !== '') {
            score = score + 1
          }
          if (val !== null || val === '') {
            submitData.append(`${value}`, val)
          }
        } else {
          score = score + 1
        }
      })
      if (score < 1) {
        return
      }

      this.saveThinking = true

      submitData.append(`page_id`, this.post_id)

      axios.post('/api/auth/update-page', submitData).then((response) => {
        this.saveThinking = false
        if (response.data.success) {
          this.getPost(this.post_id)
          this.$set(this, 'canSubmit', false)
        }

        if (response.data.menu) {
          this.dispatchSetMenu(response.data.menu)
        }
        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
        if (response.data.settings) {
          EventBus.$emit('reloadWebsiteSettings')
        }
      })
    },

    deletePage() {
      if (!this.post_id) {
        return
      }

      this.$set(this, 'deleteThinking', true)

      axios.get('/api/auth/delete-page/' + this.post_id).then((response) => {
        if (response.data.success) {
          this.$router.push({ path: '/manager/pages' })
        }

        if (response.data.menu) {
          this.dispatchSetMenu(response.data.menu)
        }

        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }

        this.$set(this, 'deleteThinking', true)
        this.$set(this, 'showDeleteModal', false)
      })
    },

    setPageData(post) {
      this.$set(this, 'update', [])
      this.$set(this, 'canSubmit', false)

      this.$set(this, 'page_title', post.title)
      this.$set(this, 'page_title_original', post.title)

      this.$set(this, 'page_status', post.status)
      this.$set(this, 'page_status_original', post.status)

      if (post.status === 0) {
        this.$set(this, 'page_status', post.status)
        this.$set(this, 'publish_status', false)
      } else {
        this.$set(this, 'publish_status', true)
      }
      this.$set(this, 'page_status', post.status)

      this.$set(this, 'page_body', post.body)
      this.$set(this, 'page_body_original', post.body)

      this.$set(this, 'page_image_featured', post.image_featured)
      this.$set(this, 'page_image_featured_original', post.image_featured)
      this.$set(this, 'previewImage', post.image_featured)

      this.$set(this, 'page_link_title', post.link_title)
      this.$set(this, 'page_link_title_original', post.link_title)

      this.$set(this, 'page_link_url', post.link_url)
      this.$set(this, 'page_link_url_original', post.link_url)
    },

    getPost(post_id) {
      // Get this post...
      axios.get('/api/post/' + post_id).then((response) => {
        let result = response.data

        if (result.success) {
          if (result.post.external_link) {
            this.$router.push({ name: 'Pages' })
          }
          this.setPageData(result.post)
          this.$set(this, 'loaded', true)
        } else {
          //... We could not find this post --- revert back to posts page ...
          this.$router.push({ name: 'Pages' })
        }
      })
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}
</style>

<style>
/*
* Style for nestable
*/
.nestable {
  position: relative;
}
.nestable-rtl {
  direction: rtl;
}
.nestable .nestable-list {
  margin: 0;
  padding: 0 0 0 40px;
  list-style-type: none;
}
.nestable-rtl .nestable-list {
  padding: 0 40px 0 0;
}
.nestable > .nestable-list {
  padding: 0;
}
.nestable-item,
.nestable-item-copy {
  margin: 10px 0 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
  margin-top: 0;
}
.nestable-item .nestable-list,
.nestable-item-copy .nestable-list {
  margin-top: 10px;
}
.nestable-item {
  position: relative;
}
.nestable-item.is-dragging .nestable-list {
  pointer-events: none;
}
.nestable-item.is-dragging * {
  opacity: 0;
  filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
  content: ' ';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(106, 127, 233, 0.274);
  border: 1px dashed rgb(73, 100, 241);
  -webkit-border-radius: 5px;
  border-radius: 5px;
}
.nestable-drag-layer {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
  left: auto;
  right: 0;
}
.nestable-drag-layer > .nestable-list {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
  padding: 0;
}
.nestable [draggable='true'] {
  /* cursor: move; */
}
.nestable-handle {
  /* display: inline; */
}
</style>
